'use client'

import { useCallback, useState } from 'react'

/**
 * Compartir la experiencia: diálogo nativo si lo hay, portapapeles si no.
 *
 * El enlace que sale es la canónica del idioma en el que se está viendo la
 * página, así que quien lo reciba abre la misma versión y no la española por
 * defecto.
 */

/** Cuánto se mantiene el «enlace copiado» tras compartir sin diálogo nativo. */
const SHARE_FEEDBACK_MS = 2500

export type ShareMethod = 'web_share' | 'clipboard'

export interface TrackerShare {
  /** True mientras se muestra el aviso de «enlace copiado». */
  feedback: boolean
  share: () => void
}

/**
 * Devuelve por dónde salió el enlace, o `null` si el usuario canceló el diálogo
 * nativo y tampoco había portapapeles disponible.
 */
async function shareOrCopy(data: {
  title: string
  text: string
  url: string
}): Promise<ShareMethod | null> {
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share(data)
      return 'web_share'
    } catch {
      // Cancelar el diálogo nativo no es un fallo: se cae al portapapeles.
    }
  }

  try {
    await navigator.clipboard.writeText(`${data.text} ${data.url}`)
    return 'clipboard'
  } catch {
    // Sin portapapeles (contexto no seguro) no queda nada que hacer.
    return null
  }
}

export function useTrackerShare({
  title,
  text,
  url,
  onShared,
}: {
  title: string
  text: string
  url: string
  onShared: (method: ShareMethod) => void
}): TrackerShare {
  const [feedback, setFeedback] = useState(false)

  const share = useCallback(() => {
    void shareOrCopy({ title, text, url }).then((method) => {
      if (!method) return
      onShared(method)
      if (method === 'clipboard') {
        setFeedback(true)
        window.setTimeout(() => setFeedback(false), SHARE_FEEDBACK_MS)
      }
    })
  }, [onShared, text, title, url])

  return { feedback, share }
}
